/* eslint-disable react/jsx-no-target-blank */
import { useRef, useState, useCallback, useMemo, memo } from "react";

import { routes } from "@/common/utils/routes";
import PlayIcon from "@/common/images/icons/play.svg?react";
import LogoImage from "@/common/images/icons/logos/logo-full-red.svg";
import {
  copyGifLink,
  getGifFixedEmbedCode,
  getGifResponsiveEmbedCode,
  sendGifToReddit,
  sendGifToTwitter
} from "@/common/utils/share";
import { EmbedDialog } from "@/components/popups/EmbedDialog";
import { ProgressBar } from "@/components/gifPreview/ProgressBar";
import { Video } from "@/components/UI/Video";

import { IMenuItem } from "../PopupMenu";
import { ShareGifMenu } from "../ShareGifMenu";
import { UserInfo } from "../UserInfo";
import { Actions, VideoButtons } from "../VideoButtons";

import "./EmbedVideoPlayer.scss";
import { IGif } from "@/common/services/types";

import { metricsBus, EventSource } from "@/common/buses";
import { useUserStore } from "@/common/store/userStore";

import { createHlsUrl } from "@/common/utils/urls";
import { useSfw } from "@/common/hooks/useSfw";
import { useEmbedFullScreen } from "./hooks/useEmbedFullScreen";

const SECONDS = 1000;
const SECONDS_PER_MINUTE = 60;
const MAX_MINUTE = 10;

interface EmbeddedVideoPlayerProps {
  gif: IGif;
  id: string;
  userName?: string;
  userId?: string;
  userPic?: string;
  sdVideo: string;
  hdVideo?: string;
  poster: string;
  sound: boolean;
  verified: boolean;
  isStudio: boolean;
}

export const EmbeddedVideoPlayer = memo((props: EmbeddedVideoPlayerProps) => {
  const currentUser = useUserStore((state) => state.currentUser);
  const [isPlaying, setPlaying] = useState<boolean>(true);
  const [isMuted, setMuted] = useState<boolean>(true);
  const [isHD, setHD] = useState<boolean>(false);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [showEmbed, setShowEmbed] = useState<boolean>(false);

  // Video progress tracking.
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  const ref = useRef<HTMLVideoElement>(null);
  const docRef = useRef<HTMLDivElement>(null);

  const startLoadTimeRef = useRef(0);

  const { sanitizeGif, isLoading } = useSfw();
  const { gif, replacement } = sanitizeGif(props.gif);

  const {
    isSupported: hasFullscreenSupport,
    isFullscreen,
    toggleFullscreen
  } = useEmbedFullScreen({ targetRef: docRef });

  const videoUrl = useMemo(() => {
    if (gif.hls) {
      return createHlsUrl(replacement?.id ?? gif.id, isHD ? "hd" : "sd");
    }

    return isHD ? props.hdVideo : props.sdVideo;
  }, [isHD, gif, props.sdVideo, props.hdVideo, replacement?.id]);

  // update video time, seek bar progress, pause video on seek
  const handleSeekBarChange = useCallback((time: number) => {
    if (Number.isFinite(time) && ref.current) {
      ref.current.currentTime = time / SECONDS;
    }

    setProgress(time);
  }, []);

  // play video on seek end
  const handleSeekBarChangeEnd = useCallback(() => {}, []);

  // video duration update
  const handleLoadedMetadata = useCallback(() => {
    if (ref.current) {
      setDuration(ref.current.duration * SECONDS);
    }
  }, []);

  // update seek bar progress on video playing
  const handleTimeUpdate = useCallback(() => {
    if (ref.current) {
      setProgress(ref.current.currentTime * SECONDS);
    }
  }, []);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / SECONDS_PER_MINUTE);
    const seconds = Math.floor(time % SECONDS_PER_MINUTE);
    return `${minutes < MAX_MINUTE ? "0" : ""}${minutes}:${seconds < MAX_MINUTE ? "0" : ""}${seconds}`;
  }, []);

  const curTimeFormatted = useMemo(() => {
    if (!ref.current) {
      return "00:00";
    }
    const remainingTime = (duration - progress);

    if (remainingTime < 0) {
      return "00:00";
    }

    return formatTime(remainingTime / SECONDS);
  }, [ref, formatTime, progress, duration]);

  const onClick = () => {
    if (ref.current) {
      if (ref.current.paused) {
        const playPromise = ref.current.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setPlaying(true);
            })
            .catch((e) => {
              console.warn("failed to play video", e);
            });
        } else {
          setPlaying(true);
        }
      } else {
        ref.current.pause();
        setPlaying(false);
      }
    }
  };

  const onAction = useCallback((action: Actions) => {
    switch (action) {
    case Actions.Mute:
      setMuted(true);
      break;
    case Actions.UnMute:
      setMuted(false);
      break;
    case Actions.FullScreen:
      toggleFullscreen();
      break;
    case Actions.HD:
      setHD(true);
      break;
    case Actions.SD:
      setHD(false);
      break;
    case Actions.Share:
      setShowShare(true);
      break;
    case Actions.View:
      window.open(routes.watch(props.id), "_blank");
      break;
    default:
      console.warn(`Unknown action: ${action}`);
    }
  }, [props.id, toggleFullscreen]);

  const onShareClick = useCallback((item: IMenuItem) => {
    switch (item.id) {
    case "link":
      copyGifLink(gif);
      break;
    case "embed":
      setShowEmbed(true);
      break;
    case "reddit":
      sendGifToReddit(gif);
      break;
    case "twitter":
      sendGifToTwitter(gif.id);
      break;
    case "discord":
      copyGifLink(gif);
      break;
    default:
      console.warn(`Unknown share action: ${item.id}`);
    }
    setShowShare(false);
  }, [gif]);

  const onShareClose = useCallback(() => {
    setShowShare(false);
  }, []);

  const onCloseEmbed = useCallback(() => {
    setShowEmbed(false);
  }, []);

  const sendMetrics = useCallback(() => {
    metricsBus.emit("gifView", {
      gifId: props.id,
      tags: [],
      source: EventSource.Embed,
      sourceId: null,
      position: 1,
      boosted: !!gif.cta,
      userName: currentUser?.username,
      fullscreen: isFullscreen,
      orientation: screen.orientation?.type,
    });
  }, [props.id, gif, isFullscreen, currentUser]);

  const handlePlay = useCallback(() => {
    sendMetrics();
  }, [sendMetrics]);

  const handleLoadStart = useCallback(() => {
    startLoadTimeRef.current = performance.now();
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <div className="embeddedPlayer" ref={docRef}>
      <a
        href={routes.watch(props.id)}
        target="_blank"
        className="videoLink"
      >
        <Video
          videoRef={ref}
          controls={false}
          autoPlay={true}
          loop
          playsInline
          poster={props.poster}
          muted={isMuted}
          onLoadedMetadata={handleLoadedMetadata}
          disablePictureInPicture
          onClick={onClick}
          onTimeUpdate={handleTimeUpdate}
          onPlay={handlePlay}
          onLoadStart={handleLoadStart}
          src={videoUrl}
        />
      </a>

      <div className="backdropWrap">
        <img
          src={props.poster}
          alt="Poster for GIF"
        />
      </div>

      <a
        href="https://www.redgifs.com/"
        target="_blank"
        title="Open RedGIFs home page"
        className="logo"
      >
        <img src={LogoImage} width="127" height="23" alt="" />
      </a>

      <div className="playerBottom">
        <div className="playerInfoAndProgress">
          <UserInfo
            name={props.userName}
            userpic={props.userPic}
            verified={props.verified}
            isStudio={props.isStudio}
            userId={props.userId}
          />
          <div className="progressBar">
            <ProgressBar
              max={duration}
              currentTime={progress}
              onChange={handleSeekBarChange}
              onChangeEnd={handleSeekBarChangeEnd}
            />
            <span className="countdown">{curTimeFormatted}</span>
          </div>
        </div>
        <VideoButtons
          hasSound={props.sound}
          muted={isMuted}
          hd={isHD}
          haveFullScreen={hasFullscreenSupport}
          fullScreen={isFullscreen}
          onClick={onAction}
        />
      </div>

      {!isPlaying && (
        <PlayIcon
          className="playIcon"
          onClick={onClick}
        />
      )}

      {showShare && (
        <ShareGifMenu
          onClick={onShareClick}
          onClose={onShareClose}
        />
      )}

      <EmbedDialog
        isOpen={showEmbed}
        fixedEmbedCode={getGifFixedEmbedCode(gif)}
        responsiveEmbedCode={getGifResponsiveEmbedCode(gif)}
        onClose={onCloseEmbed}
      />
    </div>
  );
});
